import React, {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';

import { AuthContext } from './AuthContext';

const RAW_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').trim();

function resolveApiBaseUrl() {
    if (!RAW_API_BASE_URL) return '';
    const localhostApiPattern = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i;
    if (!localhostApiPattern.test(RAW_API_BASE_URL)) {
        return RAW_API_BASE_URL;
    }
    // Prevent production pages from calling user's local machine API.
    if (typeof window !== 'undefined') {
        const host = window.location.hostname || '';
        const isLocalPage = host === 'localhost' || host === '127.0.0.1';
        if (!isLocalPage) return '';
    }
    return RAW_API_BASE_URL;
}

export const API_BASE_URL = resolveApiBaseUrl();

export const ApiContext = createContext(null);

export function ApiProvider({ children }) {
    const { token, refreshToken, updateTokens, logout } = useContext(AuthContext);
    const [status, setStatus] = useState(null);
    // Shared promise to prevent concurrent token refresh race conditions
    const refreshPromiseRef = useRef(null);

    const showStatus = useCallback((type, message) => {
        setStatus({ type, message });
        clearTimeout(showStatus._timer);
        showStatus._timer = setTimeout(() => setStatus(null), 6000);
    }, []);

    const authHeader = useMemo(() => {
        if (!token) return {};
        return { Authorization: `Bearer ${token}` };
    }, [token]);

    const parseError = useCallback(async (response) => {
        let message = `Request failed: ${response.status}`;
        let fieldErrors = null;

        // Read the body ONCE as text — a reverse-proxy error (e.g. a 504 Gateway
        // Timeout) returns an HTML page, not JSON. Previously this called
        // response.json() first and, on parse failure, fell back to
        // response.text() on the SAME already-consumed Response, which throws
        // "Failed to execute 'text' on 'Response': body stream already read".
        let text = '';
        try {
            text = await response.text();
        } catch (readError) {
            return new Error(message);
        }

        try {
            const data = text ? JSON.parse(text) : null;
            const detail = data?.detail ?? data;
            if (Array.isArray(detail)) {
                fieldErrors = detail.map((item) => ({
                    field: Array.isArray(item.loc) ? item.loc[item.loc.length - 1] : null,
                    message: item.msg || String(item),
                }));
                message = fieldErrors.map((item) => item.message).join('; ');
            } else if (detail !== null && detail !== undefined) {
                message = typeof detail === 'string' ? detail : JSON.stringify(detail);
            }
        } catch (parseErr) {
            if (text) message = text;
        }

        const err = new Error(message);
        err.status = response.status;
        const retryAfter = response.headers?.get?.('Retry-After');
        if (retryAfter) err.retryAfter = parseInt(retryAfter, 10) || null;
        if (fieldErrors) err.fieldErrors = fieldErrors;
        return err;
    }, []);

    const buildSessionExpiredError = useCallback(() => (
        new Error('Сессия истекла. Войдите снова.')
    ), []);

    const refreshSession = useCallback(async () => {
        if (!refreshToken) {
            return null;
        }

        // If a refresh is already in flight, reuse that promise to avoid
        // concurrent refreshes that would trigger token rotation conflicts.
        if (refreshPromiseRef.current) {
            return refreshPromiseRef.current;
        }

        refreshPromiseRef.current = (async () => {
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refresh_token: refreshToken }),
            });

            if (!response.ok) {
                logout();
                throw buildSessionExpiredError();
            }

            const data = await response.json();
            updateTokens(data);
            return data;
        })().finally(() => {
            refreshPromiseRef.current = null;
        });

        return refreshPromiseRef.current;
    }, [refreshToken, updateTokens, logout, buildSessionExpiredError]);

    // options.silent — отказ 403 для запроса ожидаем (например, приглашения кабинета, которые видит только
    // владелец): красный тост не показываем, ошибку обрабатывает вызывающий код.
    const request = useCallback(async (path, requestOptions = {}, retry = true) => {
        const { silent, ...options } = requestOptions;
        const accessToken = token || localStorage.getItem('anylex_token');
        const isFormData = options.body instanceof FormData;
        const isRefreshRequest = path === '/api/v1/auth/refresh';
        const requestHeaders = {
            ...(!isRefreshRequest && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
            ...(options.headers || {}),
        };
        if (options.body && !isFormData) {
            requestHeaders['Content-Type'] = 'application/json';
        }
        const response = await fetch(`${API_BASE_URL}${path}`, {
            ...options,
            headers: requestHeaders,
        });

        if (response.status === 401 && !isRefreshRequest) {
            if (retry && refreshToken) {
                const refreshed = await refreshSession();
                const freshToken = refreshed?.access_token || localStorage.getItem('anylex_token');
                const retryIsFormData = options.body instanceof FormData;
                const retryHeaders = {
                    ...(freshToken ? { Authorization: `Bearer ${freshToken}` } : {}),
                    ...(options.headers || {}),
                };
                if (options.body && !retryIsFormData) {
                    retryHeaders['Content-Type'] = 'application/json';
                }
                return request(path, {
                    ...options,
                    silent,
                    headers: retryHeaders,
                }, false);
            }

            logout();
            throw buildSessionExpiredError();
        }

        if (!response.ok) {
            const err = await parseError(response);
            if (response.status === 403 && !silent) {
                showStatus('error', err.message);
            }
            throw err;
        }

        if (response.status === 204) return null;
        return response.json();
    }, [token, refreshToken, refreshSession, parseError, logout, buildSessionExpiredError, showStatus]);

    const authFetch = useCallback(async (url, options = {}, retry = true) => {
        const accessToken = token || localStorage.getItem('anylex_token');
        const isRefreshUrl = url.endsWith('/api/v1/auth/refresh');
        const headers = {
            ...options.headers,
            ...(!isRefreshUrl && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        };

        if (options.body && !(options.body instanceof FormData)) {
            headers['Content-Type'] = 'application/json';
        }

        const response = await fetch(url, { ...options, headers });

        if (response.status === 401 && !isRefreshUrl) {
            if (retry && refreshToken) {
                const refreshed = await refreshSession();
                const freshToken = refreshed?.access_token || localStorage.getItem('anylex_token');
                const retryHeaders = {
                    ...options.headers,
                    ...(freshToken ? { Authorization: `Bearer ${freshToken}` } : {}),
                };
                if (options.body && !(options.body instanceof FormData)) {
                    retryHeaders['Content-Type'] = 'application/json';
                }
                return authFetch(url, { ...options, headers: retryHeaders }, false);
            }

            logout();
            throw buildSessionExpiredError();
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.detail || 'Request failed');
        }

        if (response.status === 204) return null;
        return response.json();
    }, [token, refreshToken, refreshSession, logout, buildSessionExpiredError]);

    // Скачивание файла (PDF, XLSX, вложения). Токен живёт 15 минут, поэтому, как и
    // request(), при 401 обновляем сессию и повторяем — иначе после паузы кнопка
    // «Скачать» падала, пока токен не обновит какой-нибудь другой запрос.
    const fetchBlob = useCallback(async (path, options = {}, retry = true) => {
        const url = /^https?:/i.test(path) ? path : `${API_BASE_URL}${path}`;
        const accessToken = token || localStorage.getItem('anylex_token');
        const response = await fetch(url, {
            ...options,
            headers: { ...(options.headers || {}), ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}) },
        });
        if (response.status === 401) {
            if (retry && refreshToken) {
                await refreshSession();
                return fetchBlob(path, options, false);
            }
            logout();
            throw buildSessionExpiredError();
        }
        if (!response.ok) throw await parseError(response);
        return response.blob();
    }, [token, refreshToken, refreshSession, logout, buildSessionExpiredError, parseError]);

    const value = useMemo(() => ({
        API_BASE_URL,
        request,
        authFetch,
        fetchBlob,
        authHeader,
        status,
        setStatus,
        showStatus,
        refreshSession,
    }), [request, authFetch, fetchBlob, authHeader, status, showStatus, refreshSession]);

    return (
        <ApiContext.Provider value={value}>
            {children}
        </ApiContext.Provider>
    );
}
